// keyrd_console/src/components/PatientList.js
import React, { useEffect, useState, useMemo, useCallback } from "react";
import { buildKeyrdHeaders } from "../api/headers";

function formatDateTime(value) {
  if (!value) return "-";
  try {
    const d = new Date(value);
    if (Number.isNaN(d.getTime())) return value;
    return d.toISOString().replace("T", " ").replace("Z", "Z");
  } catch {
    return value;
  }
}

function daysSince(value) {
  if (!value) return null;
  try {
    const d = new Date(value);
    if (Number.isNaN(d.getTime())) return null;
    const now = Date.now();
    const diffMs = now - d.getTime();
    if (diffMs < 0) return 0;
    return Math.floor(diffMs / (1000 * 60 * 60 * 24));
  } catch {
    return null;
  }
}

function formatSince(value) {
  const d = daysSince(value);
  if (d === null) return "—";
  if (d === 0) return "today";
  if (d === 1) return "1 day ago";
  return `${d} days ago`;
}

function patientStatus(patient) {
  if (patient.frozen) {
    const reason = patient.freeze_reason || patient.freeze_state_type || "";
    return reason ? `Frozen — ${reason}` : "Frozen";
  }
  if (patient.active) {
    return "Active";
  }
  return "Inactive";
}

function PatientList({ auth, selectedPatientId, onSelectPatient, onCreatePatient }) {
  const { baseUrl, token, authorized } = auth;

  const [patients, setPatients] = useState([]);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState(null);

  const [filter, setFilter] = useState("");
  const [quickFilter, setQuickFilter] = useState("all"); // "all" | "attention" | "frozen"

  // Invite panel state
  const [showInvitePanel, setShowInvitePanel] = useState(false);
  const [inviteName, setInviteName] = useState("");
  const [inviteExternalId, setInviteExternalId] = useState("");
  const [inviteNote, setInviteNote] = useState("");
  const [inviteSubmitting, setInviteSubmitting] = useState(false);
  const [inviteError, setInviteError] = useState(null);

  const ATTENTION_FEEDBACK_DAYS = 14;

  // --------------------------------------------------
  // Load patients (reusable)
  // --------------------------------------------------
  const loadPatients = useCallback(async () => {
    if (!authorized || !baseUrl) return;

    setLoading(true);
    setErr(null);

    try {
      const res = await fetch(`${baseUrl}/console/patients`, {
        headers: buildKeyrdHeaders({ token }),
      });

      if (!res.ok) {
        throw new Error(`HTTP ${res.status}`);
      }

      const json = await res.json();
      const rows = Array.isArray(json.patients) ? json.patients : [];
      setPatients(rows);
    } catch (e) {
      setErr(e.message || String(e));
    } finally {
      setLoading(false);
    }
  }, [authorized, baseUrl, token]);

  useEffect(() => {
    if (!authorized || !baseUrl) return;
    void loadPatients();
  }, [authorized, baseUrl, loadPatients]);

  // --------------------------------------------------
  // Derive urgency / attention state per patient
  // --------------------------------------------------
  const derivedPatients = useMemo(() => {
    return patients.map((p) => {
      const lastNudgeDays = daysSince(p.last_nudge_at);
      const lastFeedbackDays = daysSince(p.last_feedback_at);

      let needsAttention = false;
      let attentionReason = "";

      if (!p.frozen && p.active) {
        if (!p.last_nudge_at) {
          needsAttention = true;
          attentionReason = "Never nudged";
        } else if (!p.last_feedback_at) {
          needsAttention = true;
          attentionReason = "No feedback yet";
        } else if (
          lastFeedbackDays !== null &&
          lastFeedbackDays >= ATTENTION_FEEDBACK_DAYS
        ) {
          needsAttention = true;
          attentionReason = `No feedback in ${lastFeedbackDays} days`;
        }
      }

      return {
        ...p,
        lastNudgeDays,
        lastFeedbackDays,
        needsAttention,
        attentionReason,
      };
    });
  }, [patients]);

  // --------------------------------------------------
  // Quick filters + text filter + sort by urgency
  // --------------------------------------------------
  const filteredPatients = useMemo(() => {
    const f = (filter || "").toLowerCase();

    let rows = derivedPatients.filter((p) => {
      // quick filter
      if (quickFilter === "attention" && !p.needsAttention) return false;
      if (quickFilter === "frozen" && !p.frozen) return false;

      if (!f) return true;

      const haystack =
        [
          p.id,
          p.freeze_reason,
          p.freeze_state_type,
          p.last_response_type,
        ]
          .filter(Boolean)
          .join(" ")
          .toLowerCase() || "";

      return haystack.includes(f);
    });

    // Sort: attention first, then frozen, then newest enrollments
    rows = rows.slice().sort((a, b) => {
      if (a.needsAttention !== b.needsAttention) {
        return a.needsAttention ? -1 : 1;
      }
      if (a.frozen !== b.frozen) {
        return a.frozen ? -1 : 1;
      }

      const aCreated = new Date(a.created_at || 0).getTime();
      const bCreated = new Date(b.created_at || 0).getTime();
      return bCreated - aCreated;
    });

    return rows;
  }, [derivedPatients, filter, quickFilter]);

  const attentionCount = useMemo(
    () => derivedPatients.filter((p) => p.needsAttention).length,
    [derivedPatients]
  );
  const frozenCount = useMemo(
    () => derivedPatients.filter((p) => p.frozen).length,
    [derivedPatients]
  );

  const handleRowClick = (patientId) => {
    if (typeof onSelectPatient === "function") {
      // eslint-disable-next-line no-console
      console.log("[PatientList] row clicked:", patientId);
      onSelectPatient(patientId);
    }
  };

  const handleCreatePatientClick = () => {
    if (typeof onCreatePatient === "function") {
      onCreatePatient();
      return;
    }
    setInviteError(null);
    setShowInvitePanel(true);
  };

  const resetInviteForm = () => {
    setInviteName("");
    setInviteExternalId("");
    setInviteNote("");
    setInviteError(null);
  };

  const handleCloseInvitePanel = () => {
    if (inviteSubmitting) return;
    setShowInvitePanel(false);
    resetInviteForm();
  };

  // --------------------------------------------------
  // Submit invite / create patient
  // --------------------------------------------------
  const handleSubmitInvite = async (e) => {
    e.preventDefault();
    if (!authorized || !baseUrl || !token) return;

    setInviteSubmitting(true);
    setInviteError(null);

    const body = {
      // align these with your backend; this is a sensible default
      display_name: inviteName || null,
      external_id: inviteExternalId || null,
      note: inviteNote || null,
    };

    try {
      const res = await fetch(`${baseUrl}/console/patients/invite`, {
        method: "POST",
        headers: buildKeyrdHeaders({ token, contentType: "application/json" }),
        body: JSON.stringify(body),
      });

      let data = {};
      try {
        data = await res.json();
      } catch {
        // tolerate empty body
      }

      if (!res.ok || data.ok === false) {
        const msg =
          (data && data.error) ||
          `HTTP ${res.status} while creating / inviting patient`;
        setInviteError(msg);
        return;
      }

      // Expecting backend to return patient info; adjust keys as needed
      const createdPatient = data.patient || data.created_patient || null;

      // Refresh list
      await loadPatients();

      // Close panel + reset
      setShowInvitePanel(false);
      resetInviteForm();

      // Auto-select new patient if possible
      if (createdPatient && createdPatient.id && onSelectPatient) {
        onSelectPatient(createdPatient.id);
      }
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error("Error creating / inviting patient:", error);
      setInviteError("Network error while creating / inviting patient.");
    } finally {
      setInviteSubmitting(false);
    }
  };

  // --------------------------------------------------
  // Render
  // --------------------------------------------------
  return (
    <div style={{ position: "relative" }}>
      {/* Header row: title + primary actions */}
      <div
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          marginBottom: 8,
        }}
      >
        <div>
          <h1 style={{ fontSize: 20, fontWeight: 600, marginBottom: 4 }}>
            Patient List
          </h1>
          <p style={{ fontSize: 13, color: "#4b5563", margin: 0 }}>
            Panel of all patients in your current org. Click a row to make that
            patient active for Profile, Behavior History, and Nudge Plan.
          </p>
        </div>
        {authorized && (
          <button
            type="button"
            onClick={handleCreatePatientClick}
            style={{
              padding: "6px 14px",
              borderRadius: 999,
              border: "1px solid #0b3d2e",
              backgroundColor: "#0b3d2e",
              color: "#ffffff",
              fontSize: 12,
              cursor: "pointer",
              whiteSpace: "nowrap",
            }}
          >
            Add / invite patient
          </button>
        )}
      </div>

      {!authorized && (
        <div
          style={{
            padding: 10,
            borderRadius: 8,
            border: "1px solid #f97316",
            backgroundColor: "#fffbeb",
            fontSize: 13,
            marginBottom: 12,
          }}
        >
          Paste a valid RDN Console bearer token in the top bar and click{" "}
          <strong>Check RDN</strong> to load your patient panel.
        </div>
      )}

      {authorized && err && (
        <div
          style={{
            padding: 8,
            borderRadius: 6,
            border: "1px solid #fecaca",
            backgroundColor: "#fef2f2",
            fontSize: 12,
            color: "#b91c1c",
            marginBottom: 12,
          }}
        >
          Error loading patients: {err}
        </div>
      )}

      {authorized && !err && (
        <div
          style={{
            display: "flex",
            alignItems: "center",
            marginBottom: 12,
            gap: 8,
            flexWrap: "wrap",
          }}
        >
          {/* Search box */}
          <input
            type="text"
            value={filter}
            onChange={(e) => setFilter(e.target.value)}
            placeholder="Search by patient ID, freeze reason, or response type…"
            style={{
              flex: 1,
              minWidth: 240,
              maxWidth: 420,
              padding: "6px 8px",
              fontSize: 12,
              borderRadius: 6,
              border: "1px solid #d1d5db",
              outline: "none",
            }}
          />

          {/* Quick filters */}
          <div
            style={{
              display: "flex",
              gap: 4,
              flexWrap: "wrap",
              fontSize: 12,
            }}
          >
            <button
              type="button"
              onClick={() => setQuickFilter("all")}
              style={{
                padding: "4px 8px",
                borderRadius: 999,
                border:
                  quickFilter === "all"
                    ? "1px solid #0b3d2e"
                    : "1px solid #d1d5db",
                backgroundColor:
                  quickFilter === "all" ? "#0b3d2e" : "#ffffff",
                color: quickFilter === "all" ? "#ffffff" : "#374151",
                cursor: "pointer",
              }}
            >
              All ({patients.length})
            </button>
            <button
              type="button"
              onClick={() => setQuickFilter("attention")}
              style={{
                padding: "4px 8px",
                borderRadius: 999,
                border:
                  quickFilter === "attention"
                    ? "1px solid #b45309"
                    : "1px solid #d1d5db",
                backgroundColor:
                  quickFilter === "attention" ? "#fffbeb" : "#ffffff",
                color:
                  quickFilter === "attention" ? "#92400e" : "#374151",
                cursor: "pointer",
              }}
            >
              Needs attention ({attentionCount})
            </button>
            <button
              type="button"
              onClick={() => setQuickFilter("frozen")}
              style={{
                padding: "4px 8px",
                borderRadius: 999,
                border:
                  quickFilter === "frozen"
                    ? "1px solid #991b1b"
                    : "1px solid #d1d5db",
                backgroundColor:
                  quickFilter === "frozen" ? "#fef2f2" : "#ffffff",
                color: quickFilter === "frozen" ? "#991b1b" : "#374151",
                cursor: "pointer",
              }}
            >
              Frozen ({frozenCount})
            </button>
          </div>

          {/* Counts */}
          <div style={{ fontSize: 12, color: "#6b7280" }}>
            Showing {filteredPatients.length} of {patients.length} patients
          </div>
        </div>
      )}

      {authorized && loading && <div>Loading patients…</div>}

      {authorized &&
        !loading &&
        !err &&
        filteredPatients.length === 0 &&
        patients.length === 0 && (
          <div
            style={{
              padding: 10,
              borderRadius: 8,
              border: "1px dashed #9ca3af",
              backgroundColor: "#f9fafb",
              fontSize: 13,
            }}
          >
            No patients found in this org scope yet.
          </div>
        )}

      {authorized &&
        !loading &&
        !err &&
        filteredPatients.length === 0 &&
        patients.length > 0 && (
          <div
            style={{
              padding: 10,
              borderRadius: 8,
              border: "1px dashed #9ca3af",
              backgroundColor: "#f9fafb",
              fontSize: 13,
              marginTop: 4,
            }}
          >
            No patients match this filter or quick view. Clear filters to see
            all.
          </div>
        )}

      {authorized && !loading && !err && filteredPatients.length > 0 && (
        <div
          style={{
            marginTop: 8,
            borderRadius: 10,
            backgroundColor: "#ffffff",
            boxShadow: "0 1px 2px rgba(15,23,42,0.06)",
            border: "1px solid #e5e7eb",
            overflow: "hidden",
          }}
        >
          <table
            style={{
              width: "100%",
              borderCollapse: "collapse",
              fontSize: 12,
            }}
          >
            <thead>
              <tr
                style={{
                  textAlign: "left",
                  backgroundColor: "#f9fafb",
                  borderBottom: "1px solid #e5e7eb",
                }}
              >
                <th style={{ padding: "8px 10px", width: "28%" }}>Patient</th>
                <th style={{ padding: "8px 10px", width: "22%" }}>Status</th>
                <th style={{ padding: "8px 10px", width: "18%" }}>
                  Last nudge
                </th>
                <th style={{ padding: "8px 10px", width: "18%" }}>
                  Last feedback
                </th>
                <th style={{ padding: "8px 10px", width: "14%" }}>
                  Last response
                </th>
              </tr>
            </thead>
            <tbody>
              {filteredPatients.map((p) => {
                const isSelected = selectedPatientId === p.id;
                const statusText = patientStatus(p);

                return (
                  <tr
                    key={p.id}
                    onClick={() => handleRowClick(p.id)}
                    style={{
                      cursor: "pointer",
                      backgroundColor: isSelected ? "#ecfdf5" : "transparent",
                      borderBottom: "1px solid #f3f4f6",
                    }}
                  >
                    <td style={{ padding: "8px 10px" }}>
                      <div
                        style={{
                          fontSize: 12,
                          fontWeight: 600,
                          marginBottom: 2,
                          wordBreak: "break-all",
                        }}
                      >
                        {p.id}
                      </div>
                      <div
                        style={{
                          fontSize: 11,
                          color: "#6b7280",
                          whiteSpace: "nowrap",
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                        }}
                      >
                        Enrolled: {formatDateTime(p.created_at)}
                      </div>
                    </td>

                    <td style={{ padding: "8px 10px" }}>
                      <div
                        style={{
                          display: "inline-flex",
                          alignItems: "center",
                          padding: "2px 8px",
                          borderRadius: 999,
                          fontSize: 11,
                          fontWeight: 500,
                          backgroundColor: p.frozen
                            ? "#fef2f2"
                            : p.active
                            ? "#e6f4ea"
                            : "#f3f4f6",
                          color: p.frozen
                            ? "#991b1b"
                            : p.active
                            ? "#166534"
                            : "#4b5563",
                          border: p.frozen
                            ? "1px solid #fecaca"
                            : p.active
                            ? "1px solid #bbf7d0"
                            : "1px solid #e5e7eb",
                          marginBottom: 2,
                        }}
                      >
                        {statusText}
                      </div>
                      {p.needsAttention && (
                        <div
                          style={{
                            fontSize: 11,
                            color: "#b45309",
                            marginTop: 2,
                          }}
                        >
                          Needs attention: {p.attentionReason}
                        </div>
                      )}
                      {p.freeze_reason && (
                        <div
                          style={{
                            fontSize: 11,
                            color: "#b91c1c",
                            marginTop: 2,
                          }}
                        >
                          {p.freeze_reason}
                        </div>
                      )}
                    </td>

                    <td style={{ padding: "8px 10px" }}>
                      <div>{formatSince(p.last_nudge_at)}</div>
                      <div
                        style={{
                          fontSize: 11,
                          color: "#9ca3af",
                          marginTop: 2,
                        }}
                      >
                        {formatDateTime(p.last_nudge_at)}
                      </div>
                    </td>

                    <td style={{ padding: "8px 10px" }}>
                      <div>{formatSince(p.last_feedback_at)}</div>
                      <div
                        style={{
                          fontSize: 11,
                          color: "#9ca3af",
                          marginTop: 2,
                        }}
                      >
                        {formatDateTime(p.last_feedback_at)}
                      </div>
                    </td>

                    <td style={{ padding: "8px 10px" }}>
                      <div>{p.last_response_type || "-"}</div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {authorized && (
        <div
          style={{
            marginTop: 8,
            fontSize: 11,
            color: "#6b7280",
          }}
        >
          Selected patient: {selectedPatientId || "none"}
        </div>
      )}

      {/* Invite / Add patient panel */}
      {authorized && showInvitePanel && (
        <div
          style={{
            position: "fixed",
            inset: 0,
            backgroundColor: "rgba(15,23,42,0.35)",
            display: "flex",
            justifyContent: "flex-end",
            zIndex: 40,
          }}
        >
          <div
            style={{
              width: "360px",
              maxWidth: "90vw",
              height: "100%",
              backgroundColor: "#ffffff",
              boxShadow: "-4px 0 12px rgba(15,23,42,0.2)",
              padding: "16px 18px",
              display: "flex",
              flexDirection: "column",
            }}
          >
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                marginBottom: 8,
              }}
            >
              <div>
                <div
                  style={{
                    fontSize: 16,
                    fontWeight: 600,
                    marginBottom: 2,
                  }}
                >
                  Add / invite patient
                </div>
                <div
                  style={{
                    fontSize: 12,
                    color: "#6b7280",
                  }}
                >
                  Create a patient record and generate an invite code from the
                  backend.
                </div>
              </div>
              <button
                type="button"
                onClick={handleCloseInvitePanel}
                disabled={inviteSubmitting}
                style={{
                  border: "none",
                  background: "none",
                  fontSize: 20,
                  lineHeight: 1,
                  cursor: inviteSubmitting ? "default" : "pointer",
                  color: "#6b7280",
                }}
                aria-label="Close"
              >
                ×
              </button>
            </div>

            <form
              onSubmit={handleSubmitInvite}
              style={{ display: "flex", flexDirection: "column", flex: 1 }}
            >
              <div style={{ flex: 1, overflowY: "auto" }}>
                <div style={{ marginBottom: 10 }}>
                  <label
                    style={{
                      display: "block",
                      fontSize: 12,
                      color: "#374151",
                      marginBottom: 3,
                    }}
                  >
                    Patient name / label
                  </label>
                  <input
                    type="text"
                    value={inviteName}
                    onChange={(e) => setInviteName(e.target.value)}
                    placeholder="e.g., HS-01, Case Western athlete, etc."
                    style={{
                      width: "100%",
                      padding: "6px 8px",
                      fontSize: 12,
                      borderRadius: 6,
                      border: "1px solid #d1d5db",
                    }}
                  />
                </div>

                <div style={{ marginBottom: 10 }}>
                  <label
                    style={{
                      display: "block",
                      fontSize: 12,
                      color: "#374151",
                      marginBottom: 3,
                    }}
                  >
                    External ID / chart ID
                  </label>
                  <input
                    type="text"
                    value={inviteExternalId}
                    onChange={(e) => setInviteExternalId(e.target.value)}
                    placeholder="Optional: MRN, EMR ID, or site ID"
                    style={{
                      width: "100%",
                      padding: "6px 8px",
                      fontSize: 12,
                      borderRadius: 6,
                      border: "1px solid #d1d5db",
                    }}
                  />
                </div>

                <div style={{ marginBottom: 10 }}>
                  <label
                    style={{
                      display: "block",
                      fontSize: 12,
                      color: "#374151",
                      marginBottom: 3,
                    }}
                  >
                    Notes for this patient / site
                  </label>
                  <textarea
                    value={inviteNote}
                    onChange={(e) => setInviteNote(e.target.value)}
                    rows={3}
                    placeholder="Optional: cohort, trial arm, or referral details."
                    style={{
                      width: "100%",
                      padding: "6px 8px",
                      fontSize: 12,
                      borderRadius: 6,
                      border: "1px solid #d1d5db",
                      resize: "vertical",
                    }}
                  />
                </div>

                {inviteError && (
                  <div
                    style={{
                      fontSize: 12,
                      color: "#b91c1c",
                      backgroundColor: "#fef2f2",
                      border: "1px solid #fecaca",
                      borderRadius: 6,
                      padding: "6px 8px",
                      marginTop: 4,
                    }}
                  >
                    {inviteError}
                  </div>
                )}
              </div>

              <div
                style={{
                  display: "flex",
                  justifyContent: "flex-end",
                  gap: 8,
                  marginTop: 12,
                }}
              >
                <button
                  type="button"
                  onClick={handleCloseInvitePanel}
                  disabled={inviteSubmitting}
                  style={{
                    padding: "6px 10px",
                    borderRadius: 999,
                    border: "1px solid #d1d5db",
                    backgroundColor: "#ffffff",
                    color: "#374151",
                    fontSize: 12,
                    cursor: inviteSubmitting ? "default" : "pointer",
                  }}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={inviteSubmitting}
                  style={{
                    padding: "6px 12px",
                    borderRadius: 999,
                    border: "1px solid #0b3d2e",
                    backgroundColor: inviteSubmitting ? "#64748b" : "#0b3d2e",
                    color: "#ffffff",
                    fontSize: 12,
                    cursor: inviteSubmitting ? "default" : "pointer",
                  }}
                >
                  {inviteSubmitting ? "Creating…" : "Create & generate invite"}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}

export default PatientList;
